import { useLayoutEffect } from "react";
import { useLocation } from "react-router-dom";

const REVEAL_CLASS = "scroll-reveal-item";
const VISIBLE_CLASS = "scroll-reveal-item--visible";

const ScrollRevealManager = () => {
  const location = useLocation();

  useLayoutEffect(() => {
    const activePage = document.querySelector<HTMLElement>(
      "[data-route-active='true']"
    );

    if (!activePage) return;

    const sections = Array.from(
      activePage.querySelectorAll<HTMLElement>(
        "section:not([data-scroll-reveal='false'])"
      )
    );

    // The first screen is rendered immediately. Only the blocks below it are
    // animated, which avoids creating a full-screen composited layer during a
    // route change.
    const items = sections.slice(1).flatMap((section) => {
      if (section.dataset.scrollRevealGroup === "true") {
        return [section];
      }

      return Array.from(section.children).filter(
        (child): child is HTMLElement => child instanceof HTMLElement
      );
    });

    if (!items.length) return;

    const reduceMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;

    if (reduceMotion || !("IntersectionObserver" in window)) {
      items.forEach((item) => item.classList.add(VISIBLE_CLASS));
      return;
    }

    items.forEach((item) => item.classList.add(REVEAL_CLASS));

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;

          entry.target.classList.add(VISIBLE_CLASS);
          observer.unobserve(entry.target);
        });
      },
      {
        threshold: 0.06,
        rootMargin: "0px 0px -7% 0px",
      }
    );

    const observeFrame = window.requestAnimationFrame(() => {
      items.forEach((item) => observer.observe(item));
    });

    return () => {
      window.cancelAnimationFrame(observeFrame);
      observer.disconnect();
      items.forEach((item) => {
        item.classList.remove(REVEAL_CLASS, VISIBLE_CLASS);
      });
    };
  }, [location.key]);

  return null;
};

export default ScrollRevealManager;
